import { FC } from "react";
import { InputMode, Message, ResponseMode } from "./types";
import useOpener from "../../../../../../hooks/useOpener";
import TerminateModal from "../../../../components/modal/TerminateModal";
import CountdownTimer from "./CountdownTimer";
import useFiBiVoiceBottomBar from "./useFiBiVoiceBottomBar";
import { getIsMediaSourceSupported } from "./data";
import { useSnapshot } from "valtio";
import { businessFibiClientStore } from "../../../../../../store/businessFibiClient";
import { Settings, Mic, MicOff } from "lucide-react";
import { AudioVisualizer } from "./AudioVisualizer";
import { SettingsModal } from "./SettingsModal";
import { FaRss } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";

export interface VoiceInteractionProps {
  onModeChange?: (inputMode: InputMode, responseMode: ResponseMode) => void;
  onMicrophoneToggle?: (isActive: boolean) => void;
  onSessionEnd?: () => void;
  isBusinessMode?: boolean;
}
//
export interface FiBiVoiceBottomBarProps {
  onAddToChatHistory: (value: Message) => void;
  allMessages: Message[];
  businessProps?: {
    onEnd: () => Promise<any>;
    currentSession: string;
    businessId?: string;
    activeSession: string | null;
  };
  normalProps?: {
    onRestart: () => void;
    deleteLoading: boolean;
  };
  audioRef: React.MutableRefObject<HTMLAudioElement>;
  audioContextRef: React.MutableRefObject<AudioContext | null>;
  onInterim: (second: string) => void;
  isDirectUser: boolean;
}

const FiBiVoiceBottomBar: FC<FiBiVoiceBottomBarProps> = (props) => {
  const {
    isListening,
    stopListening,
    startListening,
    fibiSpeaking,
    fibiProcessing,
    networkStatus,
    //
    setIsSettingsOpen,
    showTextInput,
    textareaRef,
    message,
    onVoiceTextMsg,
    handleKeyPress,
    showVoiceInput,
    inputMode,
    responseMode,
    isSettingsOpen,
    handleResponseModeChange,
    handleInputModeChange,
    handleSendMessage,
    disableInputBtns,
  } = useFiBiVoiceBottomBar(props);
  const isMediaSourceSupported = getIsMediaSourceSupported();
  const isBusinessProps = !!props.businessProps;
  const { open, onOpen, onClose } = useOpener();
  const { storedCurrentSessionStartTime } = useSnapshot(
    businessFibiClientStore
  );

  return (
    <>
      <div className="w-full min-h-[130px] flex-none relative">
        <div className="relative flex flex-col h-full overflow-hidden bg-white border border-gray-200 shadow-lg rounded-2xl">
          {/* Voice State Indicators */}
          {disableInputBtns && (
            <div className="absolute top-0 left-0 right-0 px-4 py-2 border-b border-gray-100 bg-linear-to-r from-blue-50 to-purple-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {isListening && (
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-red-600">
                        Listening (Click on stop before you can send)...
                      </span>
                      <AudioVisualizer
                        audioLevel={0}
                        isActive={isListening}
                        type="input"
                        className="w-16 h-6"
                      />
                    </div>
                  )}

                  {(fibiSpeaking || fibiProcessing) && (
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-blue-600">
                        {fibiSpeaking
                          ? "FiBi Speaking..."
                          : "FiBi Processing..."}
                      </span>
                      {fibiSpeaking && (
                        <AudioVisualizer
                          audioLevel={0}
                          isActive={fibiSpeaking}
                          type="output"
                          className="w-16 h-6"
                        />
                      )}
                    </div>
                  )}
                </div>
                {isListening && (
                  <button
                    onClick={stopListening}
                    className="text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Stop
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Main Input Area */}
          <div
            className={`flex items-center space-x-3 p-4 ${
              disableInputBtns ? "pt-16" : ""
            }`}
          >
            {/* Settings Button */}
            <button
              onClick={() => setIsSettingsOpen(true)}
              className="shrink-0 p-2 text-gray-500 transition-colors duration-200 rounded-lg hover:text-gray-700 hover:bg-gray-100"
              aria-label="Voice settings"
            >
              <Settings className="w-5 h-5" />
            </button>

            {/* Text Input */}
            {showTextInput && (
              <div className="flex-1 min-w-0">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => onVoiceTextMsg(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    "Type your message or click the microphone to speak..."
                  }
                  disabled={isListening}
                  rows={1}
                  className="w-full text-base leading-6 text-gray-900 placeholder-gray-500 bg-transparent border-b outline-hidden resize-none max-h-32 disabled:opacity-50"
                  style={{ minHeight: "50px" }}
                />
              </div>
            )}

            {/* Voice-only mode placeholder */}
            {!showTextInput && (
              <div className="flex-1 min-w-0 py-3">
                <span className="text-base text-gray-500">
                  {isListening ? "Listening..." : "Click microphone to speak"}
                </span>
              </div>
            )}

            {/* Voice Input Button */}
            {showVoiceInput && (
              <button
                onClick={startListening}
                disabled={disableInputBtns}
                className={`shrink-0 p-2 rounded-lg transition-all duration-200 ${
                  isListening
                    ? "bg-red-500 text-white shadow-lg animate-pulse"
                    : disableInputBtns
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600 shadow-md"
                }`}
                aria-label={isListening ? "Stop recording" : "Start recording"}
              >
                {disableInputBtns ? (
                  <MicOff className="w-5 h-5" />
                ) : (
                  <Mic className="w-5 h-5" />
                )}
              </button>
            )}

            {/* Send Button */}
            {showTextInput && (
              <button
                onClick={handleSendMessage}
                disabled={disableInputBtns}
                className={`shrink-0 p-2 rounded-lg transition-colors duration-200 ${
                  disableInputBtns
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
                aria-label="Send message"
              >
                <IoMdSend className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Mode Indicators */}
          <div className="px-4 pb-3 mt-auto">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center space-x-4">
                <div className="flex flex-col gap-2 font-bold text-gray-800">
                  {!isMediaSourceSupported && (
                    <div
                      className={`${
                        networkStatus === "stable"
                          ? "bg-green-50"
                          : networkStatus === "unstable"
                          ? "bg-yellow-50"
                          : "bg-red-50"
                      } flex items-center gap-2 px-3 py-1 rounded-full transition-all duration-300 text-xs`}
                    >
                      <FaRss
                        className={`${
                          networkStatus === "stable"
                            ? "text-green-500"
                            : networkStatus === "unstable"
                            ? "text-yellow-500"
                            : "text-red-400"
                        } ${
                          networkStatus !== "disconnected"
                            ? "animate-pulse"
                            : ""
                        }`}
                      />
                      <span
                        className={`${
                          networkStatus === "stable"
                            ? "text-green-500"
                            : networkStatus === "unstable"
                            ? "text-yellow-500"
                            : "text-red-400"
                        }`}
                      >
                        Network:{" "}
                        {networkStatus === "stable"
                          ? "Stable"
                          : networkStatus === "unstable"
                          ? "Unstable"
                          : "Disconnected"}
                      </span>
                    </div>
                  )}
                </div>

                <span>Input: {inputMode}</span>
                <span>Response: {responseMode}</span>

                <span className="tracking-widest text-[#0076E9]">
                  {isBusinessProps &&
                    props.businessProps &&
                    storedCurrentSessionStartTime && (
                      <CountdownTimer
                        storedCurrentSessionStartTime={
                          storedCurrentSessionStartTime
                        }
                        onEnd={props.businessProps.onEnd}
                      />
                    )}
                </span>
              </div>

              <button className="text-red-500 underline" onClick={onOpen}>
                End session
              </button>
            </div>
          </div>
        </div>
      </div>

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        inputMode={inputMode}
        responseMode={responseMode}
        onInputModeChange={handleInputModeChange}
        onResponseModeChange={handleResponseModeChange}
      />

      {open && props.businessProps && (
        <TerminateModal
          title="Are you sure you want to end this session?"
          subText="This session will be ended immediately. This action is irreversible."
          onClose={onClose}
          onConfirm={props.businessProps.onEnd}
          isLoading={false}
        />
      )}
    </>
  );
};

export default FiBiVoiceBottomBar;
